/**
 * Something changes on the server while the page sits open: an invoice is paid on Stripe's page in
 * another tab, a review call booked on HubSpot's page is recorded by the sync. The component asks
 * again by itself instead of waiting for a reload.
 *
 * The component provides:
 *   - `liveCheckWanted` (computed): true while the page waits for that change
 *   - `liveCheck()` (method): one quiet request that applies the change when it is there; returns a Promise
 *
 * Checks run every `everyMs` while the tab is visible and at once when the tab is shown again. They
 * stop after `forMs` on an untouched tab (coming back to it starts them again) and for good once
 * `liveCheckWanted` turns false.
 */
export function liveCheckMixin ({ everyMs = 4000, forMs = 10 * 60 * 1000 } = {}) {
  return {
    data () {
      return {
        liveCheckTimer: null,
        liveCheckUntil: 0,
        liveCheckBusy: false,
      }
    },
    watch: {
      liveCheckWanted: {
        handler (wanted) {
          if (wanted) this.startLiveCheck()
          else this.stopLiveCheck()
        },
        immediate: true,
      },
    },
    mounted () {
      document.addEventListener('visibilitychange', this.onLiveCheckTabShown)
      window.addEventListener('focus', this.onLiveCheckTabShown)
    },
    beforeDestroy () {
      document.removeEventListener('visibilitychange', this.onLiveCheckTabShown)
      window.removeEventListener('focus', this.onLiveCheckTabShown)
      this.stopLiveCheck()
    },
    methods: {
      startLiveCheck () {
        this.stopLiveCheck()
        this.liveCheckUntil = Date.now() + forMs
        this.liveCheckTimer = setInterval(this.runLiveCheck, everyMs)
      },
      stopLiveCheck () {
        if (this.liveCheckTimer) clearInterval(this.liveCheckTimer)
        this.liveCheckTimer = null
      },
      /** Back on this tab (usually from the payment or booking page): check now and keep checking for a while. */
      onLiveCheckTabShown () {
        if (document.hidden || !this.liveCheckWanted) return
        this.startLiveCheck()
        this.runLiveCheck()
      },
      runLiveCheck () {
        if (!this.liveCheckWanted || document.hidden || this.liveCheckBusy) return null
        if (Date.now() > this.liveCheckUntil) {
          this.stopLiveCheck()
          return null
        }
        this.liveCheckBusy = true
        return Promise.resolve()
          .then(() => this.liveCheck())
          .catch(() => null)
          .finally(() => {
            this.liveCheckBusy = false
          })
      },
    },
  }
}
